import { useEffect, useState } from "react";
import { api, formatPrice } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { RstScoreBadge } from "@/components/RstScoreBadge";
import { toast } from "sonner";
import {
  ShoppingCart, CheckCircle2, Gavel, RotateCcw, Timer, Coins, PlusCircle, Trash2, Save, Sliders, Trophy,
} from "lucide-react";

const PERK_CATALOG = ["badge", "visibility", "free_ads", "advanced_stats", "priority_support", "priority_features"];

const MetricCard = ({ Icon, label, value, sub }) => (
  <div className="rounded-2xl bg-card border border-border p-5" data-testid={`rst-metric-${label}`}>
    <span className="h-10 w-10 rounded-xl bg-secondary/10 flex items-center justify-center"><Icon className="h-5 w-5 text-secondary" /></span>
    <p className="mt-3 font-heading font-extrabold text-2xl text-secondary">{value}</p>
    <p className="text-sm text-muted-foreground">{label}</p>
    {sub != null && <p className="text-xs text-primary font-medium mt-0.5">{sub}</p>}
  </div>
);

const RuleEditor = ({ role, cfg, onChange }) => {
  const { t } = useT();
  const numericKeys = Object.keys(cfg).filter((k) => k !== "tiers");
  const setWeight = (k, v) => onChange({ ...cfg, [k]: Number(v) || 0 });
  const setTier = (i, field, v) => {
    const tiers = cfg.tiers.map((tr, idx) => (idx === i ? { ...tr, [field]: field === "min" ? Number(v) || 0 : v } : tr));
    onChange({ ...cfg, tiers });
  };
  const addTier = () => onChange({ ...cfg, tiers: [...cfg.tiers, { name: "", min: 0, perks: [] }] });
  const removeTier = (i) => onChange({ ...cfg, tiers: cfg.tiers.filter((_, idx) => idx !== i) });
  const togglePerk = (i, perk) => {
    const tiers = cfg.tiers.map((tr, idx) => {
      if (idx !== i) return tr;
      const cur = tr.perks || [];
      return { ...tr, perks: cur.includes(perk) ? cur.filter((p) => p !== perk) : [...cur, perk] };
    });
    onChange({ ...cfg, tiers });
  };

  return (
    <div className="rounded-2xl bg-card border border-border p-5" data-testid={`rst-rules-${role}`}>
      <h4 className="font-heading font-bold text-lg text-secondary">{t(`rst.${role}Rules`)}</h4>
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mt-4 mb-2">{t("rst.weights")}</p>
      <div className="grid gap-3">
        {numericKeys.map((k) => (
          <div key={k} className="flex items-center gap-3">
            <label className="flex-1 text-sm text-foreground/80">{t(`rst.fields.${k}`)}</label>
            <Input type="number" value={cfg[k]} onChange={(e) => setWeight(k, e.target.value)} className="w-24" data-testid={`rst-weight-${role}-${k}`} />
          </div>
        ))}
      </div>
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mt-5 mb-2">{t("rst.tiers")}</p>
      <div className="grid gap-4">
        {cfg.tiers.map((tr, i) => (
          <div key={i} className="rounded-xl border border-border/60 p-3" data-testid={`rst-tier-${role}-${i}`}>
            <div className="flex items-center gap-2">
              <Input value={tr.name} placeholder={t("rst.tierName")} onChange={(e) => setTier(i, "name", e.target.value)} className="flex-1" data-testid={`rst-tier-name-${role}-${i}`} />
              <Input type="number" value={tr.min} placeholder={t("rst.tierMin")} onChange={(e) => setTier(i, "min", e.target.value)} className="w-28" data-testid={`rst-tier-min-${role}-${i}`} />
              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => removeTier(i)} data-testid={`rst-tier-remove-${role}-${i}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {PERK_CATALOG.map((perk) => {
                const on = (tr.perks || []).includes(perk);
                return (
                  <button
                    key={perk}
                    type="button"
                    onClick={() => togglePerk(i, perk)}
                    data-testid={`rst-perk-${role}-${i}-${perk}`}
                    className={`px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${on ? "bg-secondary text-secondary-foreground border-secondary" : "bg-transparent text-muted-foreground border-border hover:border-secondary"}`}
                  >
                    {t(`perks.${perk}`)}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" className="mt-3 rounded-full" onClick={addTier} data-testid={`rst-add-tier-${role}`}>
        <PlusCircle className="h-4 w-4 mr-1.5" /> {t("rst.addTier")}
      </Button>
    </div>
  );
};

export const RstAdminPanel = () => {
  const brand = useBrand();
  const { t } = useT();
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [missions, setMissions] = useState(null);
  const [protection, setProtection] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/rst-stats").then((r) => setStats(r.data));
    api.get("/rst-config").then((r) => setConfig(r.data));
    api.get("/missions-config").then((r) => setMissions(r.data));
    api.get("/protection-config").then((r) => setProtection(r.data));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/rst-config", config);
      await api.put("/missions-config", missions);
      await api.put("/protection-config", protection);
      toast.success(t("rst.saved"));
      load();
    } finally { setSaving(false); }
  };

  const reset = async () => {
    const r = await api.post("/rst-config/reset");
    const rm = await api.post("/missions-config/reset");
    setConfig(r.data);
    setMissions(rm.data);
    setProtection({ tier1_max: 500000, tier1_rate: 0.03, tier2_cap: 25000 });
    toast.success(t("rst.saved"));
    load();
  };

  const setProt = (k, v) => setProtection((p) => ({ ...p, [k]: Number(v) || 0 }));

  const setMission = (role, i, field, v) => {
    setMissions((m) => ({
      ...m,
      [role]: m[role].map((mi, idx) => idx === i ? { ...mi, [field]: field === "enabled" ? v : (Number(v) || 0) } : mi),
    }));
  };

  if (!stats || !config || !missions || !protection) return <div className="py-10 text-center text-muted-foreground">…</div>;

  return (
    <div className="space-y-10">
      {/* Dashboard */}
      <section data-testid="rst-dashboard">
        <h3 className="font-heading font-bold text-xl text-secondary">{t("rst.dashTitle")}</h3>
        <p className="text-sm text-muted-foreground">{t("rst.dashSubtitle")}</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
          <MetricCard Icon={ShoppingCart} label={t("rst.totalOrders")} value={stats.total_orders} />
          <MetricCard Icon={CheckCircle2} label={t("rst.delivered")} value={stats.delivered} />
          <MetricCard Icon={Gavel} label={t("rst.disputes")} value={stats.disputes} sub={`${stats.dispute_rate}% ${t("rst.disputeRate").toLowerCase()}`} />
          <MetricCard Icon={RotateCcw} label={t("rst.refunds")} value={stats.refunds} sub={`${stats.refund_rate}% ${t("rst.refundRate").toLowerCase()}`} />
          <MetricCard Icon={Timer} label={t("rst.avgResolution")} value={`${stats.avg_resolution_hours} ${t("rst.hours")}`} />
          <MetricCard Icon={Coins} label={t("rst.revenue")} value={formatPrice(stats.revenue_simulated, brand.currencySymbol)} />
        </div>

        <div className="mt-6 rounded-2xl bg-card border border-border p-5">
          <p className="text-sm font-semibold text-secondary mb-3">{t("rst.ordersByStatus")}</p>
          <div className="flex flex-wrap gap-2" data-testid="rst-orders-by-status">
            {Object.entries(stats.orders_by_status).map(([k, v]) => (
              <Badge key={k} className="bg-secondary/10 text-secondary border-0">{t(`order.statuses.${k}`)}: {v}</Badge>
            ))}
            {Object.keys(stats.orders_by_status).length === 0 && <span className="text-sm text-muted-foreground">—</span>}
          </div>
        </div>

        <div className="mt-6 rounded-2xl bg-card border border-border overflow-hidden">
          <p className="text-sm font-semibold text-secondary p-4 pb-2">{t("rst.topSellers")}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rst-top-sellers">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="p-3 font-medium">{t("admin.sellers")}</th>
                  <th className="p-3 font-medium">{t("rst.score")}</th>
                  <th className="p-3 font-medium">{t("rst.deliveredCol")}</th>
                  <th className="p-3 font-medium">{t("rst.disputesCol")}</th>
                  <th className="p-3 font-medium">{t("rst.ratingCol")}</th>
                </tr>
              </thead>
              <tbody>
                {stats.sellers.map((s) => (
                  <tr key={s.id} className="border-b border-border/50 last:border-0" data-testid={`rst-seller-row-${s.id}`}>
                    <td className="p-3 font-medium text-secondary">{s.name}</td>
                    <td className="p-3"><RstScoreBadge score={s.rst_score} tier={s.rst_tier} /></td>
                    <td className="p-3">{s.delivered}</td>
                    <td className="p-3">{s.disputes} ({s.dispute_rate}%)</td>
                    <td className="p-3">{s.rating_avg} ({s.rating_count})</td>
                  </tr>
                ))}
                {stats.sellers.length === 0 && (
                  <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">—</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Config editor */}
      <section data-testid="rst-config">
        <div className="flex items-center gap-2">
          <Sliders className="h-5 w-5 text-primary" />
          <h3 className="font-heading font-bold text-xl text-secondary">{t("rst.configTitle")}</h3>
        </div>
        <p className="text-sm text-muted-foreground mt-1">{t("rst.configSubtitle")}</p>
        <p className="text-xs text-muted-foreground mt-1 max-w-2xl">{t("rst.loyaltyReplaced")}</p>

        <div className="grid lg:grid-cols-2 gap-4 mt-5">
          <RuleEditor role="seller" cfg={config.seller} onChange={(c) => setConfig({ ...config, seller: c })} />
          <RuleEditor role="buyer" cfg={config.buyer} onChange={(c) => setConfig({ ...config, buyer: c })} />
        </div>

        <div className="flex flex-wrap gap-2 mt-5">
          <Button className="rounded-full bg-secondary hover:bg-secondary/90" onClick={save} disabled={saving} data-testid="rst-save-btn">
            <Save className="h-4 w-4 mr-2" /> {t("rst.save")}
          </Button>
          <Button variant="outline" className="rounded-full" onClick={reset} data-testid="rst-reset-btn">
            <RotateCcw className="h-4 w-4 mr-2" /> {t("rst.reset")}
          </Button>
        </div>
      </section>

      {/* Missions editor */}
      <section data-testid="rst-missions">
        <div className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-primary" />
          <h3 className="font-heading font-bold text-xl text-secondary">{t("missions.adminTitle")}</h3>
        </div>
        <p className="text-sm text-muted-foreground mt-1">{t("missions.adminSubtitle")}</p>
        <div className="grid lg:grid-cols-2 gap-4 mt-5">
          {["seller", "buyer"].map((role) => (
            <div key={role} className="rounded-2xl bg-card border border-border p-5" data-testid={`missions-${role}`}>
              <h4 className="font-heading font-bold text-lg text-secondary mb-3">{t(`missions.${role}Label`)}</h4>
              <div className="grid gap-2.5">
                {missions[role].map((mi, i) => (
                  <div key={mi.code} className="rounded-xl border border-border/60 p-3" data-testid={`mission-row-${role}-${mi.code}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-foreground/85">{t(`missions.labels.${mi.code}`)}</span>
                      <Switch checked={mi.enabled} onCheckedChange={(v) => setMission(role, i, "enabled", v)} data-testid={`mission-enabled-${role}-${mi.code}`} />
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      <label className="text-xs text-muted-foreground">{t("missions.bonus")}</label>
                      <Input type="number" value={mi.bonus} onChange={(e) => setMission(role, i, "bonus", e.target.value)} className="w-24 h-9" data-testid={`mission-bonus-${role}-${mi.code}`} />
                      <label className="text-xs text-muted-foreground ml-2">{t("missions.target")}</label>
                      <Input type="number" value={mi.target} onChange={(e) => setMission(role, i, "target", e.target.value)} className="w-24 h-9" data-testid={`mission-target-${role}-${mi.code}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Protection Achat settings */}
      <section data-testid="rst-protection">
        <div className="flex items-center gap-2">
          <Sliders className="h-5 w-5 text-primary" />
          <h3 className="font-heading font-bold text-xl text-secondary">{t("protection.adminTitle")}</h3>
        </div>
        <p className="text-sm text-muted-foreground mt-1">{t("protection.adminSubtitle")}</p>
        <div className="grid sm:grid-cols-3 gap-3 mt-4 max-w-2xl">
          <div>
            <label className="text-sm text-foreground/80">{t("protection.tier1Max")}</label>
            <Input type="number" value={protection.tier1_max} onChange={(e) => setProt("tier1_max", e.target.value)} className="mt-1.5" data-testid="prot-tier1-max" />
          </div>
          <div>
            <label className="text-sm text-foreground/80">{t("protection.tier1Rate")}</label>
            <Input type="number" step="0.01" value={protection.tier1_rate} onChange={(e) => setProt("tier1_rate", e.target.value)} className="mt-1.5" data-testid="prot-tier1-rate" />
          </div>
          <div>
            <label className="text-sm text-foreground/80">{t("protection.tier2Cap")}</label>
            <Input type="number" value={protection.tier2_cap} onChange={(e) => setProt("tier2_cap", e.target.value)} className="mt-1.5" data-testid="prot-tier2-cap" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-5">
          <Button className="rounded-full bg-secondary hover:bg-secondary/90" onClick={save} disabled={saving} data-testid="rst-save-btn-2">
            <Save className="h-4 w-4 mr-2" /> {t("rst.save")}
          </Button>
        </div>
      </section>
    </div>
  );
};
